import { MicrositeNav } from '@/globalNav/a11yFunctions';
import { stickyStackManager } from './headers/stickyStackManager';

document.addEventListener('DOMContentLoaded', () => {
  registerDirectMicrositeLayers();
  initMicrositeNav();
});

function registerDirectMicrositeLayers() {
  const registeredContainers = new Set();
  let layerIndex = 0;

  document.querySelectorAll('nav.microsite').forEach((micrositeNav) => {
    if (micrositeNav.closest('.sticky-top-container')) {
      return;
    }

    const micrositeContainer = micrositeNav.closest('.micrositenav.navigation');
    if (micrositeContainer && registeredContainers.has(micrositeContainer)) {
      return;
    }

    const targets = micrositeContainer ? [micrositeContainer, micrositeNav] : [micrositeNav];
    const orderElement = micrositeContainer || micrositeNav;
    stickyStackManager.registerLayer(`direct-microsite-${layerIndex++}`, targets, orderElement);

    if (micrositeContainer) {
      registeredContainers.add(micrositeContainer);
    }
  });
}

function initMicrositeNav() {
  // Initialize the MicrositeNav class
  const micrositeNav = document.querySelector('nav.microsite');
  if (micrositeNav) {
    const micrositeMainMenu = micrositeNav.querySelector('.microsite-main-menu');
    if (!micrositeMainMenu) return;
    // Cleanup any existing instance
    if (window.micrositeNav && typeof window.micrositeNav.destroy === 'function') {
      window.micrositeNav.destroy();
    }
    window.micrositeNav = new MicrositeNavigation(micrositeNav);
  }
}
class MicrositeNavigation {
  constructor(micrositeNav) {
    this.micrositeNavElement = micrositeNav.querySelector('.microsite-main-menu');
    this.mainMenuId = this.micrositeNavElement.id;
    this.micrositeMenu = new MicrositeNav(this.micrositeNavElement);
    this.mainNavHeight = 79 + 60;
    this.micrositeMenuOffcanvasBs = window.bootstrap.Offcanvas.getInstance(this.micrositeNavElement);
    this.micrositeToggleButton = micrositeNav.querySelector('.microsite-toggle');
    this.backdrop = micrositeNav.querySelector('.menu-page-backdrop');

    // Dynamic overflow handling properties
    this.navContainer = micrositeNav;
    this.navList = this.micrositeNavElement.querySelector('ul[role="menu"]');
    this.originalNavItems = [];
    this.visibleNavItems = [];
    this.overflowNavItems = [];
    this.moreDropdown = null;
    this.resizeObserver = null;
    this.resizeDebounceTimer = null;
    this.isInitialized = false;
    this.focusListenerAttached = false;
    this.boundHandleFocusIn = this.handleFocusIn.bind(this);

    this.lastZoomLevel = window.devicePixelRatio;
    this.initializeEventListeners();
    this.initializeOverflowHandling();
  }

  initializeEventListeners() {
    this.micrositeNavElement.addEventListener('hidden.bs.offcanvas', () => {
      this.hideAllLevel3Menus();
      this.micrositeMenu.mainMenu.resetMenu();
    });
    this.micrositeNavElement.addEventListener('hide.bs.offcanvas', () => {
      this.micrositeToggleButton.setAttribute('aria-expanded', 'false');
      this.micrositeToggleButton.classList.remove('open');
      this.micrositeNavElement.removeAttribute('aria-labelledby');
      // Remove z-index class from sticky elements when offcanvas closes
      this.removeZIndexFromStickyElements();
    });

    this.micrositeNavElement.addEventListener('show.bs.offcanvas', () => {
      this.micrositeMenuOffcanvasBs = window.bootstrap.Offcanvas.getInstance(this.micrositeNavElement);
      this.micrositeToggleButton.setAttribute('aria-expanded', 'true');
      this.micrositeToggleButton.classList.add('open');
      this.micrositeNavElement.setAttribute('aria-labelledby', `${this.micrositeNavElement.id}PanelLabel`);
      // Add z-index class to sticky elements when offcanvas opens
      this.addZIndexToStickyElements();
    });

    this.micrositeNavElement.addEventListener('shown.bs.offcanvas', () => {
      this.micrositeMenu.menus[this.mainMenuId].navItems[
        this.micrositeMenu.menus[this.mainMenuId].firstFocusableElement
      ].element.focus();
    });

    this.backdrop.addEventListener('click', this.resetAllMicrositeMenus.bind(this));
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    window.addEventListener('resize', this.handleViewportChange.bind(this));

    // Add focus tracking for desktop only
    this.manageFocusListener();

    Object.keys(this.micrositeMenu.menus).forEach((menu) => {
      const menuObj = this.micrositeMenu.menus[menu];
      if (menuObj.menuType === 'microsite-menu') {
        menuObj.navItems.forEach((item) => {
          if (item.element.tagName === 'BUTTON') {
            item.element.addEventListener('click', () => {
              if (this.isCloseBtn(item) && this.micrositeMenuOffcanvasBs) {
                this.micrositeMenuOffcanvasBs.hide();
              } else {
                this.handleDropDownClick(menuObj, item);
              }
            });
          }
        });
        menuObj.menuElement.addEventListener('keydown', (e) => this.micrositeNavKeyboardA11y(e));
      } else if (menuObj.menuType === 'level3Menu') {
        menuObj.menuElement.addEventListener('keydown', (e) => this.micrositeLevel3KeyboardA11y(e, menuObj));
        menuObj.navItems.forEach((item) => {
          if (item.element.tagName === 'BUTTON') {
            item.element.addEventListener('click', () => {
              if (this.isCloseBtn(item)) {
                this.handleSubMenuCloseBtnClick(menuObj);
              } else if (this.isBackBtn(item)) {
                this.handleSubMenuBackBtnClick(menuObj);
              }
            });
          }
        });
      }
    });
  }

  handleViewportChange() {
    if (window.devicePixelRatio !== this.lastZoomLevel) {
      if (this.micrositeMenuOffcanvasBs && this.micrositeNavElement.classList.contains('show')) {
        this.micrositeMenuOffcanvasBs.hide();
      }
      this.lastZoomLevel = window.devicePixelRatio;
    }
  }

  handleDropDownClick(menuObj, item) {
    const parentUl = Array.from(menuObj.menuElement.children).find((child) => child.tagName === 'UL');
    const menuButton = item.element;
    const menuDefinition = this.micrositeMenu.menus[item.controls];
    if (!menuDefinition) return;
    const menu = menuDefinition.menuElement;
    if (!menu || !parentUl) return;
    if (!menu.classList.contains('show')) {
      parentUl.classList.remove('overflow-scroll');
      this.hideAllLevel3Menus();
      menu.classList.add('show');
      // Add stop-scroll to microsite-main-menu when level3Menu opens
      this.micrositeNavElement.classList.add('stop-scroll');
      const openMenuDefinition = this.micrositeMenu.menus[menu.id];
      if (!openMenuDefinition) return;
      const firstFocusableElement = openMenuDefinition.navItems[openMenuDefinition.firstFocusableElement]?.element;
      if (!firstFocusableElement) return;
      if (firstFocusableElement.offsetParent !== null) {
        firstFocusableElement.focus();
      } else {
        while (openMenuDefinition.getCurrentItem().element.offsetParent === null) {
          openMenuDefinition.nextItem();
        }
        openMenuDefinition.getCurrentItem().element.focus();
      }
      menuButton.setAttribute('aria-expanded', 'true');
      menuObj.navItems.forEach((item) => item.element.classList.remove('active'));
      menuButton.classList.add('active');
      menuButton.setAttribute('data-layer-label', `Open ${menuButton.innerText} Menu`);
    } else {
      menu.classList.remove('show');
      menuButton.classList.remove('active');
      menu.previousElementSibling.setAttribute('aria-expanded', 'false');
      menuButton.setAttribute('data-layer-label', `Close ${menuButton.innerText} Menu`);
      this.hideAllLevel3Menus();
    }
  }

  isCloseBtn(e) {
    return e.element.classList.contains('menu-close-btn');
  }

  isBackBtn(e) {
    return e.element.classList.contains('menu-back-btn');
  }

  handleDocumentClick(event) {
    // Handle regular level3 menus
    Object.values(this.micrositeMenu.menus).forEach((menu) => {
      if (menu.menuType === 'level3Menu' && menu.menuElement.classList.contains('show')) {
        const triggerButton = menu.menuElement.previousElementSibling;
        const clickedOutside = !triggerButton.contains(event.target) && !menu.menuElement.contains(event.target);
        if (clickedOutside) {
          this.handleSubMenuCloseBtnClick(menu);
        }
      }
    });

    // Handle More dropdown
    if (this.moreDropdown && isDesktop()) {
      const moreContainer = this.moreDropdown.container;
      const moreMenu = this.moreDropdown.menu;

      // Check if click is outside the more dropdown
      if (!moreContainer.contains(event.target) && moreMenu.classList.contains('show')) {
        // Close more dropdown
        moreMenu.classList.remove('show');
        moreMenu.classList.add('d-none');
        this.moreDropdown.button.setAttribute('aria-expanded', 'false');
        this.moreDropdown.button.classList.remove('active');

        // Reset inline styles
        moreMenu.style.transform = '';
        moreMenu.style.position = '';
        moreMenu.style.top = '';
        moreMenu.style.left = '';
        moreMenu.style.zIndex = '';
      }
    }
  }

  handleFocusIn(event) {
    // Only handle focus for desktop
    if (!isDesktop()) return;

    const focusedElement = event.target;

    // Don't process if focused element is not valid
    if (!focusedElement) return;

    // Check if focus is moving out of any open level3 menus
    Object.values(this.micrositeMenu.menus).forEach((menu) => {
      if (menu.menuType === 'level3Menu' && menu.menuElement.classList.contains('show')) {
        const menuContainer = menu.menuElement;
        const menuButton = menu.menuElement.previousElementSibling;

        // Check if focused element is not within the menu or its trigger button
        if (
          !menuContainer.contains(focusedElement) &&
          !menuButton.contains(focusedElement) &&
          menuButton !== focusedElement
        ) {
          // Close the menu
          this.hidelevel3Menu(menu);
          const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === menu.menuId);
          if (mainMenuNavItem) {
            mainMenuNavItem.element.classList.remove('active');
          }
        }
      }
    });

    // Check if focus is moving out of the More dropdown
    if (this.moreDropdown && this.moreDropdown.menu.classList.contains('show')) {
      const moreContainer = this.moreDropdown.container;

      // Check if focused element is not within the more dropdown
      if (!moreContainer.contains(focusedElement)) {
        this.closeMoreDropdown();
      }
    }
  }

  manageFocusListener() {
    const shouldHaveFocusListener = isDesktop();

    if (shouldHaveFocusListener && !this.focusListenerAttached) {
      // Add focus listener for desktop
      document.addEventListener('focusin', this.boundHandleFocusIn);
      this.focusListenerAttached = true;
    } else if (!shouldHaveFocusListener && this.focusListenerAttached) {
      // Remove focus listener for mobile
      document.removeEventListener('focusin', this.boundHandleFocusIn);
      this.focusListenerAttached = false;
    }
  }

  handleSubMenuBackBtnClick(menuObj) {
    const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === menuObj.menuId);
    mainMenuNavItem.element.classList.remove('active');
    mainMenuNavItem.element.focus();
    this.hidelevel3Menu(menuObj);
  }

  handleSubMenuCloseBtnClick(menuObj) {
    if (this.micrositeMenuOffcanvasBs) {
      this.micrositeMenuOffcanvasBs.hide();
    } else {
      this.hideMenuBackDrop();
    }
    this.hidelevel3Menu(menuObj);
    const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === menuObj.menuId);
    mainMenuNavItem.element.classList.remove('active');
    mainMenuNavItem.element.focus();
  }

  hideAllLevel3Menus() {
    Object.values(this.micrositeMenu.menus).forEach((menu) => {
      if (menu.menuType === 'level3Menu') {
        if (menu.menuElement.classList.contains('show')) {
          this.hidelevel3Menu(menu);
        }
      }
    });
  }

  hidelevel3Menu(menu) {
    menu.menuElement.classList.remove('show');
    menu.menuElement.previousElementSibling.setAttribute('aria-expanded', 'false');
    menu.resetMenu();

    // Remove stop-scroll from microsite-main-menu when level3Menu closes
    // Check if any other level3 menus are still open before removing the class
    const hasOpenLevel3Menus = Object.values(this.micrositeMenu.menus).some(
      (m) => m.menuType === 'level3Menu' && m.menuElement.classList.contains('show')
    );

    if (!hasOpenLevel3Menus) {
      this.micrositeNavElement.classList.remove('stop-scroll');
    }
  }

  showMenuBackDrop() {
    this.backdrop.classList.add('show');
    // want to make sure the backdrop is not scrollable when the menu is open
    document.body.style.overflow = 'hidden';
  }

  hideMenuBackDrop() {
    this.backdrop.classList.remove('show');
    // want to make sure the page is scrollable when the menu is closed
    document.body.style.overflow = '';
  }

  resetAllMicrositeMenus() {
    this.hideMenuBackDrop();
    this.hideAllLevel3Menus();
    this.micrositeMenu.mainMenu.resetMenu();
    this.micrositeMenu.mainMenu.navItems.forEach((navItem) => {
      navItem.element.classList.remove('active');
      navItem.element.setAttribute('aria-expanded', 'false');
    });
  }
  micrositeNavKeyboardA11y(e) {
    if (!isDesktop()) {
      const panelNav = this.micrositeMenu.mainMenu;
      if (e.code === 'Escape') {
        panelNav.navItems.find((item) => item.element.classList.contains('menu-close-btn')).element.click();
      } else if (e.code === 'Tab') {
        let item = e.shiftKey ? panelNav.prevItem() : panelNav.nextItem();
        while (item.element.offsetParent === null) {
          item = e.shiftKey ? panelNav.prevItem() : panelNav.nextItem();
        }
        if (item) item.element.focus();
      }

      if (e.code === 'Escape' || e.code === 'Tab') {
        e.stopPropagation();
        e.preventDefault();
      }
    } else {
      // Desktop logic - handle Escape to close More dropdown if open
      if (e.code === 'Escape' && this.moreDropdown && this.moreDropdown.menu.classList.contains('show')) {
        this.closeMoreDropdown();
        // Return focus to the More button
        this.moreDropdown.button.focus();
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }

  micrositeLevel3KeyboardA11y(e, panelNav) {
    if (isDesktop()) {
      this.micrositeLevel3DesktopKeyboardA11y(e, panelNav);
    } else {
      if (e.code === 'Escape') {
        if (this.micrositeMenuOffcanvasBs) {
          this.micrositeMenuOffcanvasBs.hide();
        } else {
          this.hideMenuBackDrop();
        }
        this.hidelevel3Menu(panelNav);
        const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === panelNav.menuId);
        mainMenuNavItem.element.classList.remove('active');
        mainMenuNavItem.element.focus();
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (e.code !== 'Tab') return;
      if (e.target.tagName === 'INPUT' && e.target.value) {
        e.target.nextElementSibling.focus();
      } else {
        let item = e.shiftKey ? panelNav.prevItem() : panelNav.nextItem();
        while (item.element.offsetParent === null || item.element.parentElement.style.display === 'none') {
          item = e.shiftKey ? panelNav.prevItem() : panelNav.nextItem();
        }
        if (item) item.element.focus();
      }

      e.stopPropagation();
      e.preventDefault();
    }
  }

  micrositeLevel3DesktopKeyboardA11y(e, panelNav) {
    if (e.code === 'Escape') {
      if (this.micrositeMenuOffcanvasBs) {
        this.micrositeMenuOffcanvasBs.hide();
      } else {
        this.hideMenuBackDrop();
      }
      this.hidelevel3Menu(panelNav);
      const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === panelNav.menuId);
      mainMenuNavItem.element.classList.remove('active');
      mainMenuNavItem.element.focus();
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowDown') {
      let item = panelNav.nextItem();
      while (item.element.offsetParent === null || item.element.parentElement.style.display === 'none') {
        item = panelNav.nextItem();
      }
      if (item) item.element.focus();
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowUp') {
      let item = panelNav.prevItem();
      while (item.element.offsetParent === null || item.element.parentElement.style.display === 'none') {
        item = panelNav.prevItem();
      }
      if (item) item.element.focus();
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowLeft') {
      this.hidelevel3Menu(panelNav);
      const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === panelNav.menuId);
      mainMenuNavItem.element.classList.remove('active');
      let sibling = mainMenuNavItem.element.parentElement.previousElementSibling;

      if (sibling && sibling.tagName !== 'UL') {
        sibling.children[0].focus();
        e.stopPropagation();
        e.preventDefault();
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowRight') {
      this.hidelevel3Menu(panelNav);
      const mainMenuNavItem = this.micrositeMenu.mainMenu.navItems.find((item) => item.controls === panelNav.menuId);
      mainMenuNavItem.element.classList.remove('active');
      let sibling = mainMenuNavItem.element.parentElement.nextElementSibling;

      if (sibling && sibling.tagName !== 'UL') {
        sibling.children[0].focus();
        e.stopPropagation();
        e.preventDefault();
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }
  }

  // Dynamic Overflow Handling Methods
  initializeOverflowHandling() {
    // Only enable on desktop where horizontal layout is used
    if (!isDesktop()) return;

    // Store original nav items
    this.storeOriginalNavItems();

    // Create more dropdown
    this.createMoreDropdown();

    // Initialize ResizeObserver for responsive behavior
    this.initializeResizeObserver();

    // Initial calculation with a small delay to ensure DOM is settled
    setTimeout(() => {
      this.calculateOverflow();
      this.isInitialized = true;
    }, 200);
  }

  storeOriginalNavItems() {
    const navItems = Array.from(this.navList.children);
    // Exclude the More dropdown from original nav items
    this.originalNavItems = navItems
      .filter((item) => !item.classList.contains('more-dropdown-container'))
      .map((item) => ({
        element: item,
        width: 0, // Will be calculated dynamically
      }));
  }

  createMoreDropdown() {
    // Create more dropdown structure
    const moreItem = document.createElement('li');
    moreItem.className = 'gap-2 gap-xl-1 more-dropdown-container';
    moreItem.setAttribute('role', 'none');
    moreItem.style.display = 'none'; // Hidden by default

    const moreButton = document.createElement('button');
    moreButton.className = 'open-submenu gap-1 mh-emit more-dropdown-button';
    moreButton.setAttribute('type', 'button');
    moreButton.setAttribute('aria-expanded', 'false');
    moreButton.setAttribute('aria-label', 'More menu');
    moreButton.setAttribute('role', 'menuitem');
    moreButton.setAttribute('aria-haspopup', 'menu');
    moreButton.setAttribute('aria-controls', `${this.mainMenuId}-more`);
    moreButton.setAttribute('id', `${this.mainMenuId}-more-button`);
    moreButton.setAttribute('data-layer-comp', 'button');
    moreButton.setAttribute('data-layer-subarea', 'micronav');
    moreButton.setAttribute('data-layer-label', 'Open More submenu');

    const moreText = document.createElement('span');
    moreText.textContent = 'More';

    const moreIcon = document.createElement('uxt-icon');
    moreIcon.setAttribute('icon', 'chevron-right');

    moreButton.appendChild(moreText);
    moreButton.appendChild(moreIcon);

    // Create dropdown menu with Bootstrap classes
    const moreDropdownMenu = document.createElement('div');
    moreDropdownMenu.className = 'd-none more-dropdown-menu';
    moreDropdownMenu.setAttribute('id', `${this.mainMenuId}-more`);
    moreDropdownMenu.setAttribute('role', 'menu');
    moreDropdownMenu.setAttribute('aria-labelledby', `${this.mainMenuId}-more-button`);

    // Only add offcanvas header for mobile (it will be hidden on desktop via CSS)
    const moreDropdownHeader = document.createElement('div');
    moreDropdownHeader.className = 'offcanvas-header p-0 pb-2 d-xl-none';

    const moreDropdownTitle = document.createElement('h3');
    moreDropdownTitle.className = 'offcanvas-title fw-bold';
    moreDropdownTitle.textContent = 'More';

    moreDropdownHeader.appendChild(moreDropdownTitle);
    moreDropdownMenu.appendChild(moreDropdownHeader);

    const moreDropdownList = document.createElement('ul');
    moreDropdownList.setAttribute('role', 'menu');
    moreDropdownList.className = 'list-unstyled mb-0 py-2 more-items-list';

    moreDropdownMenu.appendChild(moreDropdownList);

    moreItem.appendChild(moreButton);
    moreItem.appendChild(moreDropdownMenu);

    // Add event listeners
    moreButton.addEventListener('click', () => {
      this.handleMoreDropdownClick(moreButton, moreDropdownMenu);
    });

    // Add keyboard support for More dropdown
    moreDropdownMenu.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') {
        // Close more dropdown
        moreDropdownMenu.classList.remove('show');
        moreDropdownMenu.classList.add('d-none');
        moreButton.setAttribute('aria-expanded', 'false');
        moreButton.classList.remove('active');

        // Reset inline styles
        moreDropdownMenu.style.transform = '';
        moreDropdownMenu.style.position = '';
        moreDropdownMenu.style.top = '';
        moreDropdownMenu.style.left = '';
        moreDropdownMenu.style.zIndex = '';

        // Return focus to more button
        moreButton.focus();

        e.stopPropagation();
        e.preventDefault();
      }
    });

    this.moreDropdown = {
      container: moreItem,
      button: moreButton,
      menu: moreDropdownMenu,
      list: moreDropdownList,
      text: moreText,
    };

    // Append to nav list
    this.navList.appendChild(moreItem);
  }

  handleMoreDropdownClick(button, menu) {
    const isOpen = menu.classList.contains('show');

    if (!isOpen) {
      // Close any other open level3 menus first
      this.hideAllLevel3Menus();

      // Show dropdown (not offcanvas style)
      menu.classList.add('show');
      button.setAttribute('aria-expanded', 'true');
      button.classList.add('active');

      // Position the dropdown properly for desktop
      if (isDesktop()) {
        // Remove any transform that might cause offcanvas behavior
        menu.style.transform = 'none';
        menu.style.position = 'absolute';
        menu.style.top = '100%';
        menu.style.left = '0';
        menu.style.zIndex = '1050';
      }

      // Focus first item in dropdown
      const firstItem = menu.querySelector('a, button');
      if (firstItem) {
        firstItem.focus();
      }
    } else {
      // Hide dropdown
      menu.classList.remove('show');
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('active');

      // Reset inline styles
      menu.style.transform = '';
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.zIndex = '';
    }
  }

  initializeResizeObserver() {
    // Debounced resize handler
    const handleResize = () => {
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = setTimeout(() => {
        const isCurrentlyDesktop = isDesktop();

        // Manage focus listener based on desktop/mobile state
        this.manageFocusListener();

        if (isCurrentlyDesktop && this.isInitialized) {
          this.calculateOverflow();
        } else if (!isCurrentlyDesktop) {
          // On mobile, reset to show all items and hide more dropdown
          this.resetToMobileLayout();
        }
      }, 150);
    };

    // Use ResizeObserver for better performance
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(handleResize);
      this.resizeObserver.observe(this.navContainer);
    } else {
      // Fallback to window resize
      window.addEventListener('resize', handleResize);
    }
  }

  resetToMobileLayout() {
    // Show all original nav items
    this.showAllNavItems();

    // Hide more dropdown
    if (this.moreDropdown) {
      this.moreDropdown.container.style.display = 'none';
      this.clearMoreDropdown();
    }
  }

  calculateOverflow() {
    if (!isDesktop() || !this.navList) return;

    // Reset to show all items for measurement
    this.showAllNavItems();
    this.moreDropdown.container.style.display = 'none';

    // Force layout recalculation
    this.navList.offsetWidth;

    // Calculate available width
    const containerWidth = this.navContainer.offsetWidth;

    // Account for logo and CTA area (the outside one, not the one inside offcanvas)
    const logo = this.navContainer.querySelector('a[id$="-home-link"]');
    const ctaArea = this.navContainer.querySelector('.cta-area.d-none.d-md-flex');
    const logoWidth = logo ? logo.offsetWidth : 0;
    const ctaWidth = ctaArea ? ctaArea.offsetWidth : 0;

    // Get the actual nav list container width
    const navListContainer = this.navList.parentElement;
    const navListContainerWidth = navListContainer ? navListContainer.offsetWidth : this.navList.offsetWidth;

    // Fixed More button width for consistent calculation
    const moreButtonWidth = 90;

    // Conservative buffer to prevent overlap
    const safetyBuffer = 20;

    // Use the smaller of container width calculation or nav list container width
    const baseWidth = Math.min(containerWidth - logoWidth - ctaWidth, navListContainerWidth);
    const availableWidthWithoutMore = baseWidth - safetyBuffer;

    // Measure each nav item and calculate if More button is needed
    let totalWidth = 0;
    let visibleCount = 0;
    let needsMoreButton = false;

    // Calculate gap between items (1rem = 16px for gap-xl-1 on desktop)
    const gapSize = 16;

    // First pass: check if all items fit without More button
    this.originalNavItems.forEach((item) => {
      const itemWidth = item.element.offsetWidth;
      item.width = itemWidth;
      totalWidth += itemWidth;
    });

    // Add gap widths (n-1 gaps for n items)
    if (this.originalNavItems.length > 1) {
      totalWidth += (this.originalNavItems.length - 1) * gapSize;
    }

    // Check if we need More button at all
    if (totalWidth > availableWidthWithoutMore) {
      needsMoreButton = true;
    }

    // Second pass: calculate visible items considering More button if needed
    totalWidth = 0;
    visibleCount = 0;
    const effectiveAvailableWidth = needsMoreButton
      ? availableWidthWithoutMore - moreButtonWidth
      : availableWidthWithoutMore;

    this.originalNavItems.forEach((item) => {
      const widthWithThisItem = totalWidth + item.width;
      // Add gap if this isn't the first item
      const gapsNeeded = visibleCount > 0 ? gapSize : 0;

      if (widthWithThisItem + gapsNeeded <= effectiveAvailableWidth) {
        totalWidth = widthWithThisItem + gapsNeeded;
        visibleCount++;
      }
    });

    // Final check: if we're trying to show all items but need More button, reduce by one
    if (needsMoreButton && visibleCount === this.originalNavItems.length) {
      visibleCount = Math.max(0, visibleCount - 1);
    }

    // Update visibility
    this.updateNavItemVisibility(visibleCount);
  }

  updateNavItemVisibility(visibleCount) {
    const totalItems = this.originalNavItems.length;
    const overflowCount = totalItems - visibleCount;

    if (overflowCount > 0) {
      // Show more dropdown
      this.moreDropdown.container.style.display = 'flex';

      // Update more button text (keep visible text as just "More")
      this.moreDropdown.text.textContent = 'More';

      // Update aria-label for accessibility with count information
      this.moreDropdown.button.setAttribute('aria-label', `More menu, ${overflowCount} additional items`);
      this.moreDropdown.button.setAttribute('data-layer-label', `Open More submenu, ${overflowCount} items`);

      // Move overflow items to more dropdown
      this.moveItemsToMoreDropdown(visibleCount);

      // Hide overflow items from main nav
      this.originalNavItems.forEach((item, index) => {
        if (index >= visibleCount) {
          item.element.style.display = 'none';
        } else {
          item.element.style.display = 'flex';
        }
      });
    } else {
      // Hide more dropdown
      this.moreDropdown.container.style.display = 'none';

      // Reset aria-label when no overflow
      this.moreDropdown.button.setAttribute('aria-label', 'More menu');
      this.moreDropdown.button.setAttribute('data-layer-label', 'Open More submenu');

      this.clearMoreDropdown();
      this.showAllNavItems();
    }
  }

  moveItemsToMoreDropdown(visibleCount) {
    // Clear existing items in more dropdown
    this.moreDropdown.list.innerHTML = '';

    const overflowItems = this.originalNavItems.slice(visibleCount);

    // Move overflow items
    overflowItems.forEach((item, index) => {
      // Add separator before each menu group (except the first one)
      if (index > 0) {
        this.addSeparatorToMoreDropdown();
      }

      // Check if this item has a level3Menu sibling (indicating a dropdown)
      const level3Menu = item.element.querySelector('.level3Menu');
      const originalContent = item.element.querySelector('a, button');

      if (level3Menu && originalContent) {
        // Handle dropdown items - flatten the structure
        this.createFlattenedDropdownInMore(item, originalContent, level3Menu);
      } else if (originalContent) {
        // Handle regular links
        this.createRegularLinkInMore(originalContent);
      }
    });
  }

  addSeparatorToMoreDropdown() {
    // Create separator container
    const separatorItem = document.createElement('li');
    separatorItem.setAttribute('role', 'separator');
    separatorItem.className = '';

    // Create the actual hr element
    const separator = document.createElement('hr');
    separator.className = 'dropdown-divider my-0 w-100';
    separator.setAttribute('role', 'presentation');

    separatorItem.appendChild(separator);
    this.moreDropdown.list.appendChild(separatorItem);
  }

  createFlattenedDropdownInMore(item, originalButton, level3Menu) {
    // Create group container with proper ARIA roles
    const groupContainer = document.createElement('li');
    groupContainer.setAttribute('role', 'none');

    const groupElement = document.createElement('ul');
    groupElement.setAttribute('role', 'group');
    groupElement.className = 'list-unstyled w-100';

    // Get group name from button text for aria-label
    const groupName = originalButton.textContent.replace(/\s*\u203A\s*$/, ''); // Remove chevron if present
    groupElement.setAttribute('aria-label', `${groupName} submenu items`);

    // Create group heading with Bootstrap classes
    const headingItem = document.createElement('li');
    headingItem.setAttribute('role', 'none');

    const headingElement = document.createElement('div');
    headingElement.className = 'fw-bold text-muted small text-uppercase py-2 px-3 mb-1';
    headingElement.textContent = groupName;
    headingElement.setAttribute('role', 'heading');
    headingElement.setAttribute('aria-level', '3');

    headingItem.appendChild(headingElement);
    groupElement.appendChild(headingItem);

    // Extract all links from the level3Menu
    const submenuLinks = level3Menu.querySelectorAll('a[href], button:not(.menu-close-btn):not(.menu-back-btn)');

    submenuLinks.forEach((link) => {
      const linkItem = document.createElement('li');
      linkItem.setAttribute('role', 'none');

      const clonedLink = link.cloneNode(true);

      // Update IDs to avoid conflicts
      if (clonedLink.id) {
        clonedLink.id = `more-${clonedLink.id}`;
      }

      // Apply Bootstrap classes for styling - remove submenu classes and add appropriate styling
      clonedLink.classList.remove('open-submenu');
      clonedLink.removeAttribute('aria-controls');
      clonedLink.removeAttribute('controls');
      clonedLink.removeAttribute('aria-expanded');

      // Set proper menuitem role and styling
      clonedLink.setAttribute('role', 'menuitem');

      // Add Bootstrap dropdown item styling
      if (clonedLink.tagName === 'A') {
        clonedLink.className = 'dropdown-item py-2 px-3';
      } else if (clonedLink.tagName === 'BUTTON') {
        clonedLink.className = 'dropdown-item py-2 px-3 border-0 bg-transparent text-start w-100';
      }

      // Handle click events
      if (clonedLink.tagName === 'BUTTON') {
        clonedLink.addEventListener('click', () => {
          // Trigger original button functionality
          link.click();
          this.closeMoreDropdown();
        });
      } else if (clonedLink.tagName === 'A') {
        clonedLink.addEventListener('click', () => {
          this.closeMoreDropdown();
        });
      }

      linkItem.appendChild(clonedLink);
      groupElement.appendChild(linkItem);
    });

    groupContainer.appendChild(groupElement);
    this.moreDropdown.list.appendChild(groupContainer);
  }

  createRegularLinkInMore(originalContent) {
    const listItem = document.createElement('li');
    listItem.setAttribute('role', 'none');

    const clonedContent = originalContent.cloneNode(true);

    // Update IDs to avoid conflicts
    if (clonedContent.id) {
      clonedContent.id = `more-${clonedContent.id}`;
    }

    // Set proper menuitem role
    clonedContent.setAttribute('role', 'menuitem');

    // Apply Bootstrap dropdown item styling
    if (clonedContent.tagName === 'A') {
      clonedContent.className = 'dropdown-item py-2 px-3';
    } else if (clonedContent.tagName === 'BUTTON') {
      clonedContent.className = 'dropdown-item py-2 px-3 border-0 bg-transparent text-start w-100';
    }

    // Handle click events
    if (clonedContent.tagName === 'BUTTON') {
      clonedContent.addEventListener('click', () => {
        originalContent.click();
        this.closeMoreDropdown();
      });
    } else if (clonedContent.tagName === 'A') {
      clonedContent.addEventListener('click', () => {
        this.closeMoreDropdown();
      });
    }

    listItem.appendChild(clonedContent);
    this.moreDropdown.list.appendChild(listItem);
  }

  closeMoreDropdown() {
    this.moreDropdown.menu.classList.remove('show');
    this.moreDropdown.menu.classList.add('d-none');
    this.moreDropdown.button.setAttribute('aria-expanded', 'false');
    this.moreDropdown.button.classList.remove('active');

    // Reset inline styles
    this.moreDropdown.menu.style.transform = '';
    this.moreDropdown.menu.style.position = '';
    this.moreDropdown.menu.style.top = '';
    this.moreDropdown.menu.style.left = '';
    this.moreDropdown.menu.style.zIndex = '';
  }

  showAllNavItems() {
    this.originalNavItems.forEach((item) => {
      item.element.style.display = 'flex';
    });
  }

  clearMoreDropdown() {
    this.moreDropdown.list.innerHTML = '';
  }

  // Sticky elements z-index management methods
  // Note: We need to add this because when the microsite offcanvas opens, even though the offcanvas
  // itself has a higher z-index value, the parent sticky-top elements have a lower z-index value
  // than the global nav. Adding this script updates the z-index value to be higher than the global nav
  // when the offcanvas opens, and removes it when the offcanvas closes to prevent z-index conflicts.
  addZIndexToStickyElements() {
    this.stickyElements().forEach((element) => {
      element.classList.add('z-index-header');
    });
  }

  removeZIndexFromStickyElements() {
    this.stickyElements().forEach((element) => {
      element.classList.remove('z-index-header');
    });
  }

  stickyElements() {
    return document.querySelectorAll('main .sticky-top');
  }

  // Cleanup method
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }

    // Remove focus listener if attached
    if (this.focusListenerAttached) {
      document.removeEventListener('focusin', this.boundHandleFocusIn);
      this.focusListenerAttached = false;
    }
  }
}

const isDesktop = () => window.innerWidth >= 1200;
